import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, AlertTriangle, Axe, MapPin, Truck, TreePine, Scale } from "lucide-react";
import Navigation from "../components/layout/Navigation";
import Footer from "../components/layout/Footer";
import Breadcrumb from "../components/common/Breadcrumb";
import LeadForm from "../components/forms/LeadForm";

const VendreBoisSurPiedPage = () => {
  useEffect(() => {
    document.title = "Vendre ses Bois sur Pied | Valorisation Forestière | Sylva Invest";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', "Vous souhaitez vendre ou valoriser des bois sur pied ? Sylva Invest analyse votre situation et vous oriente vers les solutions adaptées.");
    }
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'https://sylva-invest.com/vendre-bois-sur-pied');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', 'Vendre ses Bois sur Pied | Valorisation Forestière | Sylva Invest');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', "Vous souhaitez vendre ou valoriser des bois sur pied ? Sylva Invest analyse votre situation et vous oriente vers les solutions adaptées.");
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', 'https://sylva-invest.com/vendre-bois-sur-pied');
    return () => {
      document.title = "Sylva Invest — Transactions Forestières, Remembrement & Conseil (France)";
      if (metaDescription) metaDescription.setAttribute('content', 'Sylva Invest accompagne propriétaires, héritiers et investisseurs pour recomposer le foncier forestier morcelé.');
      if (canonical) canonical.setAttribute('href', 'https://sylva-invest.com/');
    };
  }, []);

  const objectiveOptions = [
    { value: "vente_bois", label: "Vendre uniquement les bois" },
    { value: "valeur", label: "Étudier la valeur des bois" },
    { value: "vente_foncier", label: "Vendre également la propriété" },
    { value: "autre", label: "Je ne sais pas encore" }
  ];

  const factors = [
    { icon: TreePine, title: "Essences", text: "La nature des arbres (chêne, hêtre, pin, douglas, etc.) influence directement la valeur des bois." },
    { icon: Scale, title: "Volume et maturité", text: "Le diamètre, la hauteur et l'âge des arbres déterminent le volume exploitable et sa qualité." },
    { icon: Truck, title: "Accessibilité", text: "La présence de chemins, la pente du terrain et la distance aux routes impactent les coûts d'exploitation." },
    { icon: MapPin, title: "Localisation", text: "La proximité des acheteurs (scieries, transformateurs) et le marché local influencent les prix." }
  ];

  return (
    <>
      <Navigation />
      <main className="pt-20">
        {/* Hero */}
        <section className="bg-forest text-white py-16 md:py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <Breadcrumb items={[{ label: "Propriétaires" }, { label: "Vendre bois sur pied" }]} />
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl"
            >
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-sans font-semibold mb-4">
                Valorisation des Bois
              </p>
              <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-normal tracking-tight mb-6" data-testid="page-h1">
                Vendre ou valoriser vos bois sur pied
              </h1>
              <p className="font-sans text-base md:text-lg text-white/80 leading-relaxed mb-8">
                Vous possédez une parcelle forestière et envisagez une coupe ou la vente de bois ? Sylva Invest peut analyser votre situation foncière et vous orienter vers des professionnels adaptés pour la partie technique et opérationnelle.
              </p>
              <a href="#formulaire" className="btn-primary inline-flex items-center">
                Étudier mes bois
                <ArrowRight className="w-4 h-4 ml-2" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* Important note */}
        <section className="py-12 bg-bone border-b border-gray-200">
          <div className="max-w-4xl mx-auto px-6 md:px-12">
            <div className="flex items-start">
              <AlertTriangle className="w-6 h-6 text-gold mr-4 mt-1 flex-shrink-0" />
              <div className="font-sans text-gray-700">
                <p className="font-medium text-forest mb-2">Notre rôle</p>
                <p className="text-sm leading-relaxed">
                  Sylva Invest n'est pas un exploitant forestier ni un gestionnaire de forêts. Nous intervenons sur le foncier forestier, les transactions et le conseil. Pour la vente de bois sur pied, nous analysons votre situation et pouvons vous mettre en relation avec les professionnels compétents (experts forestiers, exploitants, coopératives) selon vos besoins.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Factors */}
        <section className="py-16 md:py-20 bg-cream">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mb-12"
            >
              <h2 className="font-serif text-2xl md:text-3xl text-forest mb-4">
                Avant de vendre vos bois
              </h2>
              <div className="w-12 h-px bg-gold mb-6" />
              <p className="font-sans text-gray-600 leading-relaxed">
                La valeur des bois sur pied dépend de nombreux facteurs. Comprendre ces éléments vous aidera à prendre une décision éclairée.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {factors.map((factor, index) => (
                <motion.div
                  key={factor.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-6 border border-gray-100 flex items-start"
                >
                  <factor.icon className="w-8 h-8 text-gold mr-4 flex-shrink-0" />
                  <div>
                    <h3 className="font-serif text-lg text-forest mb-2">{factor.title}</h3>
                    <p className="font-sans text-sm text-gray-600">{factor.text}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-8 bg-white p-6 border border-gray-100"
            >
              <h3 className="font-serif text-lg text-forest mb-4">Autres éléments à considérer</h3>
              <ul className="space-y-3 font-sans text-sm text-gray-600">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-gold mr-3 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-forest">Les conditions d'exploitation :</strong> la période de coupe, les contraintes environnementales et les réglementations locales.</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-gold mr-3 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-forest">Le marché du bois :</strong> les cours varient selon les essences, les qualités et la conjoncture économique.</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-gold mr-3 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-forest">L'état sanitaire :</strong> les maladies ou dégâts peuvent affecter la valeur des bois.</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </section>

        {/* Form */}
        <section id="formulaire" className="py-16 md:py-20 bg-bone scroll-mt-20">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-10"
            >
              <h2 className="font-serif text-2xl md:text-3xl text-forest mb-4">
                Étudier votre situation
              </h2>
              <p className="font-sans text-gray-600">
                Décrivez votre projet et nous vous recontacterons pour analyser vos options.
              </p>
            </motion.div>

            <LeadForm
              formType="vente_bois"
              title={null}
              submitButtonText="Envoyer ma demande"
              showSurface={true}
              showCadastre={false}
              showEssences={true}
              showAccessibility={true}
              showObjective={true}
              showTiming={true}
              showForestType={true}
              showRecentCut={true}
              showSellProperty={true}
              objectiveOptions={objectiveOptions}
            />
          </div>
        </section>

        {/* Cross-sell */}
        <section className="py-12 bg-forest text-white">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <p className="font-sans text-sm text-gold mb-2">Vous envisagez également de vendre la propriété ?</p>
                <p className="font-serif text-xl">Nous pouvons étudier une transaction globale</p>
              </div>
              <Link to="/vendre-parcelle-bois" className="btn-primary bg-gold text-forest hover:bg-white hover:text-forest">
                Vendre ma forêt
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default VendreBoisSurPiedPage;
